/**
 * @fileoverview Payment History page displaying completed payments with search,
 * date filtering, and pagination. Admin-only view.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  Payment as PaymentIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  CreditCard as CardIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import api from '../../services/api';

/**
 * PaymentPaid Component - Displays paginated list of completed payments.
 *
 * Features:
 * - Search by customer name, email, phone, vehicle registration
 * - Date range filtering (start/end date)
 * - Pagination with configurable page size (10, 25, 50)
 * - Summary stats (total payments, total amount)
 * - Detail modal for individual payment view
 *
 * @returns {JSX.Element} The payment history page
 */
const PaymentPaid = () => {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const user = useSelector((state) => state.auth.user);

  // Debounce search and reset page
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCompletedPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page + 1); // API uses 1-based pagination
      params.append('page_size', rowsPerPage);

      if (debouncedSearch) {
        params.append('search', debouncedSearch);
      }
      if (startDate) {
        params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      }
      if (endDate) {
        params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      }

      const response = await api.get(`/v1/payments/completed/?${params.toString()}`);
      setPayments(response.data.results);
      setTotalCount(response.data.count);
      setSummary(response.data.summary);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch completed payments');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, startDate, endDate]);

  // Fetch payments when filters change
  useEffect(() => {
    fetchCompletedPayments();
  }, [fetchCompletedPayments]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearch('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedPayment(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const hasActiveFilters = search || startDate || endDate;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Payment History
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCompletedPayments}
          >
            Refresh
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{summary.payment_count || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Paid
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <CardIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {formatCurrency(summary.total_amount || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search customer, email, phone, vehicle..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    setPage(0);
                  }}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    setPage(0);
                  }}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                {hasActiveFilters && (
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    fullWidth
                  >
                    Clear
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Payments Table */}
        <Card>
          <CardContent>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : payments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No payments found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters
                    ? 'Try adjusting your search or filters'
                    : 'No completed payments yet'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Vehicle</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Paid At</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.payment_id}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {payment.customer?.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {payment.customer?.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <CarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {payment.vehicle?.registration}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {payment.vehicle?.make} {payment.vehicle?.model}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {formatCurrency(payment.amount)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {formatDateTime(payment.paid_at)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(payment)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Details Modal */}
        <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Payment Details</Typography>
              <CheckCircleIcon color="success" />
            </Box>
          </DialogTitle>

          <DialogContent>
            {selectedPayment && (
              <Grid container spacing={3}>
                {/* Customer Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Customer Information
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Name:</strong> {selectedPayment.customer?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Email:</strong> {selectedPayment.customer?.email}
                      </Typography>
                      {selectedPayment.customer?.phone && (
                        <Typography variant="body2">
                          <strong>Phone:</strong> {selectedPayment.customer?.phone}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Vehicle Info */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Vehicle
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Registration:</strong> {selectedPayment.vehicle?.registration}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Make:</strong> {selectedPayment.vehicle?.make}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Model:</strong> {selectedPayment.vehicle?.model}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Payment Info */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Payment Information
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Payment ID:</strong> {selectedPayment.payment_id}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Amount:</strong>{' '}
                        <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                          {formatCurrency(selectedPayment.amount)}
                        </span>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Paid At:</strong> {formatDateTime(selectedPayment.paid_at)}
                      </Typography>
                      {selectedPayment.stripe_payment_intent_id && (
                        <Typography variant="body2">
                          <strong>Stripe ID:</strong> {selectedPayment.stripe_payment_intent_id}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Items if available */}
                {selectedPayment.items && selectedPayment.items.length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Items Paid
                        </Typography>
                        <List dense>
                          {selectedPayment.items.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemText
                                primary={item.name}
                                secondary={item.part_number || 'Service'}
                              />
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(item.total || item.price || 0)}
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDetails}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PaymentPaid;
